import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import DescriptionCell from '../components/DescriptionCell';
import './Checkout.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatPrice = (p) =>
  p != null ? `₹${parseFloat(p).toFixed(2)}` : '—';

const formatDate = (date) =>
  date.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isAfterCutoffTime = (cutoffTimeStr) => {
  const now = new Date();
  if (now.getHours() < 4) return true;
  if (!cutoffTimeStr) return false;
  const [h, m] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h, m, 0, 0);
  return now >= cutoff;
};

const nextWeekend = (baseDate) => {
  const d = new Date(baseDate);
  const dow = d.getDay();
  const daysToSat = (6 - dow + 7) % 7;
  const daysToSun = (7 - dow) % 7;
  const satDate = new Date(d);
  satDate.setDate(d.getDate() + daysToSat);
  const sunDate = new Date(d);
  sunDate.setDate(d.getDate() + daysToSun);
  return satDate <= sunDate
    ? { sat: satDate, sun: sunDate }
    : { sat: sunDate, sun: satDate };
};

const computeDeliveryDates = (product) => {
  const lead = parseInt(product.order_lead_days, 10) || 0;
  const extra = isAfterCutoffTime(product.order_cutoff_time) ? 1 : 0;
  const base = addDays(new Date(), lead + extra);
  if (product.days_available === 'WE') {
    const { sat, sun } = nextWeekend(base);
    return [sat, sun];
  }
  return [base];
};

// Returns all upcoming dates matching the kit's delivery days (up to 8 weeks ahead).
// Dates are created at local noon so toISODate() returns the correct local date in IST.
const computeKitPossibleDates = (product, deliveryDaysOverride = null) => {
  const lead = parseInt(product.order_lead_days, 10) || 0;
  const extra = isAfterCutoffTime(product.order_cutoff_time) ? 1 : 0;
  const deliveryDays = deliveryDaysOverride || product.kit_delivery_days || [];
  const base = new Date();
  base.setHours(12, 0, 0, 0);
  base.setDate(base.getDate() + lead + extra);
  const daySet = new Set(deliveryDays.length ? deliveryDays : [1, 2, 3, 4, 5]);
  const max = new Date(base);
  max.setDate(max.getDate() + 56);
  const dates = [];
  const d = new Date(base);
  while (d <= max && dates.length < 20) {
    if (daySet.has(d.getDay())) dates.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return dates;
};

const formatKitDate = (d) => {
  const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${DAY_ABBR[d.getDay()]} · ${dateStr}`;
};

const windowMatchesDate = (tw, date) => {
  const dow = date.getDay();
  const abbr = DAY_ABBR[dow];
  const code = tw.days_of_week;
  if (!code || code === 'AD') return true;
  if (code === 'WD') return dow >= 1 && dow <= 5;
  if (code === 'WE') return dow === 0 || dow === 6;
  return code.split(',').map((d) => d.trim()).includes(abbr);
};

const formatSlot = (tw) =>
  tw ? `${tw.start_time.slice(0, 5)} – ${tw.end_time.slice(0, 5)}` : '—';

// Intersection of time windows across ALL items for a date.
// Returns deduplicated {start_time, end_time} objects.
const computeCommonWindows = (windowsPerItem) => {
  const constraining = windowsPerItem.filter((ws) => ws.length > 0);
  if (constraining.length === 0) return [];
  let candidates = constraining[0].map((tw) => ({ start_time: tw.start_time, end_time: tw.end_time }));
  for (let i = 1; i < constraining.length; i++) {
    const intersected = [];
    for (const cand of candidates) {
      for (const tw of constraining[i]) {
        const s = cand.start_time > tw.start_time ? cand.start_time : tw.start_time;
        const e = cand.end_time   < tw.end_time   ? cand.end_time   : tw.end_time;
        if (s < e) intersected.push({ start_time: s, end_time: e });
      }
    }
    candidates = intersected;
  }
  const seen = new Set();
  return candidates.filter((c) => {
    const k = `${c.start_time}|${c.end_time}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// Per-item windows that overlap with at least one other item ("social" windows).
const computeSocialWindows = (itemWindows, otherItemsWindows) =>
  itemWindows.filter((myWin) =>
    otherItemsWindows.some((otherWins) =>
      otherWins.some((tw) => {
        const s = myWin.start_time > tw.start_time ? myWin.start_time : tw.start_time;
        const e = myWin.end_time   < tw.end_time   ? myWin.end_time   : tw.end_time;
        return s < e;
      })
    )
  );

// Assign windowMode, socialWindows, and selectedWindow to every enriched entry.
// Per date:
//   'common' — all items share a common intersection → one group, group-level window selector.
//   'social'  — no full common window → per-item social windows, individual selectors.
// Kit products are always forced to 'social' so the pill-grid date picker always renders.
const assignWindowModes = (enrichedMap) => {
  // Group entries by date
  const byDate = {};
  Object.values(enrichedMap).forEach((e) => {
    if (!byDate[e.deliveryDateStr]) byDate[e.deliveryDateStr] = [];
    byDate[e.deliveryDateStr].push(e);
  });

  const result = {};
  Object.values(byDate).forEach((dateEntries) => {
    const common = computeCommonWindows(dateEntries.map((e) => e.windows));

    if (common.length > 0) {
      // All items share at least one common window — use group mode
      // Preserve a previously chosen common window when recomputing after a date change
      const prevSel = dateEntries.find((e) => e.selectedWindow)?.selectedWindow;
      const selected = (prevSel && common.find(
        (w) => w.start_time === prevSel.start_time && w.end_time === prevSel.end_time
      )) || common[0];
      dateEntries.forEach((e) => {
        result[e.product.id] = { ...e, windowMode: 'common', socialWindows: common, selectedWindow: selected };
      });
    } else {
      // No full common window — per-item social windows
      dateEntries.forEach((e) => {
        const otherWins = dateEntries.filter((o) => o.product.id !== e.product.id).map((o) => o.windows);
        const social = computeSocialWindows(e.windows, otherWins);
        const socialWindows = social.length > 0 ? social : [...e.windows];
        const prevSel = e.selectedWindow;
        const selected = (prevSel && socialWindows.some(
          (w) => w.start_time === prevSel.start_time && w.end_time === prevSel.end_time
        )) ? prevSel : (socialWindows[0] || null);
        result[e.product.id] = { ...e, windowMode: 'social', socialWindows, selectedWindow: selected };
      });
    }
  });
  // Kit products always use 'social' mode so their pill-grid date picker is always rendered.
  Object.keys(result).forEach((id) => {
    if (result[id].product.is_kit) {
      result[id] = { ...result[id], windowMode: 'social' };
    }
  });

  return result;
};

// In a mixed cart (kit + non-kit), derive the earliest kit delivery date and its window.
const getMinKitEntry = (enrichedMap) => {
  let minEntry = null;
  Object.values(enrichedMap).forEach((e) => {
    if (!e.product.is_kit || e.product.id === 1047) return;
    if (!minEntry || e.deliveryDateStr < minEntry.deliveryDateStr) minEntry = e;
  });
  return minEntry;
};

// Group items by delivery date, then cluster by overlapping selected windows.
// When the cart contains any kit products, ALL items go into ONE parent order so
// the server can expand kit children and create a non-kit child alongside them.
// Each kit item carries kit_schedule_id and kit_first_delivery_date so the server
// knows which schedule and first delivery date the user chose for that kit.
const buildOrderGroups = (enrichedMap) => {
  const allEntries = Object.values(enrichedMap);
  const kitEntries = allEntries.filter(e => e.product.is_kit && e.product.id !== 1047);
  const hasKits    = kitEntries.length > 0;

  if (hasKits) {
    // Single parent order for everything — server handles kit expansion and non-kit child
    const minKitEntry = getMinKitEntry(enrichedMap);
    const items = allEntries
      .filter(e => e.product.id !== 1047)
      .map(e => {
        const item = { product: e.product, quantity: e.quantity };
        if (e.product.is_kit) {
          if (e.selectedScheduleId)  item.kit_schedule_id        = e.selectedScheduleId;
          if (e.deliveryDateStr)     item.kit_first_delivery_date = e.deliveryDateStr;
        }
        return item;
      });
    return [{
      deliveryDateStr:   minKitEntry.deliveryDateStr,
      deliveryDateLabel: formatDate(minKitEntry.deliveryDate),
      slot: minKitEntry.selectedWindow ? formatSlot(minKitEntry.selectedWindow) : '—',
      items,
      total: items.reduce((s, i) => s + parseFloat(i.product.price) * i.quantity, 0),
    }];
  }

  // Pure non-kit cart: group by date then cluster by overlapping windows
  const byDate = {};
  allEntries.forEach((entry) => {
    if (!byDate[entry.deliveryDateStr]) byDate[entry.deliveryDateStr] = [];
    byDate[entry.deliveryDateStr].push(entry);
  });

  const groups = [];
  Object.entries(byDate).forEach(([dateStr, dateEntries]) => {
    const noWindow = dateEntries.filter((e) => !e.selectedWindow);

    // Non-pack items with no window → solo groups
    noWindow.filter((e) => !e.product.isPack).forEach((e) => {
      groups.push({
        deliveryDateStr: dateStr,
        deliveryDateLabel: formatDate(e.deliveryDate),
        slot: '—',
        items: [{ product: e.product, quantity: e.quantity }],
        total: parseFloat(e.product.price) * e.quantity,
      });
    });

    // Pack combos with no window → one group per menu_pack_id
    const packsByMid = {};
    noWindow.filter((e) => e.product.isPack).forEach((e) => {
      const mid = e.product.menu_pack_id;
      if (!packsByMid[mid]) packsByMid[mid] = [];
      packsByMid[mid].push(e);
    });
    Object.values(packsByMid).forEach((entries) => {
      const rep = entries[0];
      groups.push({
        deliveryDateStr: dateStr,
        deliveryDateLabel: formatDate(rep.deliveryDate),
        slot: '—',
        items: entries.map(({ product, quantity }) => ({ product, quantity })),
        total: entries.reduce((s, e) => s + parseFloat(e.product.price) * e.quantity, 0),
      });
    });

    // Items with a window: sort by start_time then greedily cluster by overlap
    const windowed = dateEntries
      .filter((e) => e.selectedWindow)
      .sort((a, b) => a.selectedWindow.start_time.localeCompare(b.selectedWindow.start_time));

    const unassigned = [...windowed];
    while (unassigned.length > 0) {
      const cluster = [unassigned.shift()];
      let ix = { start_time: cluster[0].selectedWindow.start_time, end_time: cluster[0].selectedWindow.end_time };

      let i = 0;
      while (i < unassigned.length) {
        const entry  = unassigned[i];
        const s = ix.start_time > entry.selectedWindow.start_time ? ix.start_time : entry.selectedWindow.start_time;
        const e = ix.end_time   < entry.selectedWindow.end_time   ? ix.end_time   : entry.selectedWindow.end_time;
        if (s < e) {
          cluster.push(entry);
          ix = { start_time: s, end_time: e };
          unassigned.splice(i, 1);
        } else {
          i++;
        }
      }

      groups.push({
        deliveryDateStr: dateStr,
        deliveryDateLabel: formatDate(cluster[0].deliveryDate),
        slot: formatSlot(ix),
        items: cluster.map(({ product, quantity }) => ({ product, quantity })),
        total: cluster.reduce((sum, { product, quantity }) => sum + parseFloat(product.price) * quantity, 0),
      });
    }
  });

  return groups.sort((a, b) => a.deliveryDateStr.localeCompare(b.deliveryDateStr) || a.slot.localeCompare(b.slot));
};

const r2 = (n) => Math.round(n * 100) / 100;

// Build the per-item payload sent to the backend order API.
// Pack items carry menu_pack_id + selectionSummary instead of product_id.
const buildItemPayload = ({ product, quantity, kit_schedule_id, kit_first_delivery_date }) =>
  product.isPack
    ? {
        pack_id:          product.pack_id,
        qty:              quantity,
        unit_price:       parseFloat(product.price),
        total_price:      parseFloat(product.price) * quantity,
        selectionSummary: product.selectionSummary,
      }
    : {
        product_id:      product.id,
        qty:             quantity,
        unit_price:      parseFloat(product.price),
        total_price:     parseFloat(product.price) * quantity,
        ...(kit_schedule_id        ? { kit_schedule_id }         : {}),
        ...(kit_first_delivery_date ? { kit_first_delivery_date } : {}),
      };

/* ─────────────────────────────────────────────────────── */

const Checkout = () => {
  const { cart, cartTotal, clearCart, setWaiveDeliveryFee } = useCart();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // phase: loading | slot_select | summary | placing | confirmed
  const [phase, setPhase] = useState('loading');
  const [enriched, setEnriched] = useState(null);
  const [orderGroups, setOrderGroups] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [orderError, setOrderError] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);

  // Wallet state
  const [walletBalance, setWalletBalance] = useState(null);  // { cash_balance, points_balance }
  const [walletCashUsed, setWalletCashUsed] = useState(0);
  const [walletPointsUsed, setWalletPointsUsed] = useState(0);

  // Offer / promo state
  const [promoCode, setPromoCode] = useState('');
  const [appliedOffer, setAppliedOffer] = useState(null);
  const [alternateOffer, setAlternateOffer] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  const isCS           = user?.user_type === 'CS';
  const isStorePartner = user?.user_type === 'S' || isCS;
  const isLive         = isCS || (user?.user_type === 'S' && (localStorage.getItem('checkoutMode') || 'live') === 'live');
  const liveSalesChannelId = isCS ? 23 : 3;
  const isMobile = window.innerWidth < 768;
  const bypassPayment = user?.bypass_payment === 'Y';

  // QR payment state (store partners on mobile only)
  const [qrModal, setQrModal] = useState({ open: false, qrId: null, imageUrl: null, amount: 0 });
  const qrPollRef = useRef(null);

  const items = Object.values(cart);
  const authHeader = { Authorization: `Bearer ${user?.token}` };

  // Fetch all customer addresses
  useEffect(() => {
    if (!user?.customer_id) return;
    axios
      .get(`${API_URL}/addresses/my-addresses`, { headers: authHeader })
      .then((r) => {
        setCustomerAddresses(r.data);
        const defaultAddr = r.data.find(a => a.is_default) || r.data[0];
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
          setCustomerInfo({ customer_name: user?.name, address: defaultAddr.address });
        }
      })
      .catch(() => {
        // fallback to legacy endpoint
        axios
          .get(`${API_URL}/menuRoutes/customer-address`, {
            params: { customer_id: user.customer_id },
            headers: authHeader,
          })
          .then((r) => setCustomerInfo(r.data))
          .catch(() => {});
      });
  }, [user]); // eslint-disable-line

  const selectedAddress = customerAddresses.find(a => a.id === selectedAddressId) || null;

  useEffect(() => {
    setWaiveDeliveryFee(!!selectedAddress?.waive_delivery_fee);
  }, [selectedAddress]); // eslint-disable-line

  // Filter enriched windows to 12:xx and 18:xx when address waives delivery
  const WAIVER_WINDOW_STARTS = ['12:', '18:'];
  useEffect(() => {
    if (!enriched || !selectedAddress?.waive_delivery_fee) return;
    const needsFilter = Object.values(enriched).some(e =>
      (e.socialWindows || []).some(w => !WAIVER_WINDOW_STARTS.some(s => w.start_time?.startsWith(s)))
    );
    if (!needsFilter) return;
    const updated = {};
    Object.entries(enriched).forEach(([id, e]) => {
      const filtered = (e.socialWindows || []).filter(w => WAIVER_WINDOW_STARTS.some(s => w.start_time?.startsWith(s)));
      const sel = filtered.find(w => w.start_time === e.selectedWindow?.start_time) || filtered[0] || null;
      updated[id] = { ...e, socialWindows: filtered, selectedWindow: sel };
    });
    setEnriched(updated);
  }, [enriched, selectedAddress?.waive_delivery_fee]); // eslint-disable-line

  // Compute delivery dates + load time windows
  useEffect(() => {
    if (items.length === 0) { setPhase('summary'); return; }

    if (isLive) {
      const today = new Date();
      const todayStr = toISODate(today);
      setOrderGroups([{
        deliveryDateStr: todayStr,
        deliveryDateLabel: formatDate(today),
        slot: '—',
        items: items.map(({ product, quantity }) => ({ product, quantity })),
        total: items.reduce((s, { product, quantity }) => s + parseFloat(product.price) * quantity, 0),
      }]);
      setPhase('summary');
      return;
    }

    const load = async () => {
      // Load address first so waiver status is known before building enriched state.
      // This prevents a second setEnriched call from the waiver filter effect flashing the UI.
      let waiverActive = false;
      if (user?.customer_id) {
        try {
          const addrRes = await axios.get(`${API_URL}/addresses/my-addresses`, { headers: authHeader });
          setCustomerAddresses(addrRes.data);
          const defaultAddr = addrRes.data.find(a => a.is_default) || addrRes.data[0];
          if (defaultAddr) {
            setSelectedAddressId(defaultAddr.id);
            setCustomerInfo({ customer_name: user?.name, address: defaultAddr.address });
            waiverActive = !!defaultAddr.waive_delivery_fee;
            setWaiveDeliveryFee(waiverActive);
          }
        } catch (_) {}
      }

      const nonPackItems    = items.filter(({ product }) => product.id !== 1047 && !product.isPack);
      const packItems       = items.filter(({ product }) => product.isPack);
      const deliveryFeeItem = items.find(({ product }) => product.id === 1047) || null;

      // Fetch delivery schedules for kit products
      const kitProductIds = nonPackItems.filter(({ product }) => product.is_kit).map(({ product }) => product.id);
      const kitSchedulesMap = {};
      if (kitProductIds.length > 0) {
        const schedResults = await Promise.all(
          kitProductIds.map(id =>
            axios.get(`${API_URL}/menuRoutes/public/kit-schedules/${id}`, { headers: authHeader })
              .then(r => ({ id, schedules: r.data }))
              .catch(() => ({ id, schedules: [] }))
          )
        );
        schedResults.forEach(({ id, schedules }) => { kitSchedulesMap[id] = schedules; });
      }

      const deliveryMap = {};
      nonPackItems.forEach(({ product, quantity }) => {
        const schedules = kitSchedulesMap[product.id] || [];
        const selectedScheduleId = schedules[0]?.id || null;
        const scheduleDays = schedules.find(s => s.id === selectedScheduleId)?.delivery_days || null;
        const possibleDates = product.is_kit
          ? computeKitPossibleDates(product, scheduleDays)
          : computeDeliveryDates(product);
        const selectedDate = possibleDates[0] || addDays(new Date(), 1);
        deliveryMap[product.id] = {
          product, quantity,
          possibleDates,
          selectedDate,
          deliveryDate: selectedDate,
          deliveryDateStr: toISODate(selectedDate),
          allWindows: [],
          windows: [],
          selectedWindow: null,
          schedules,
          selectedScheduleId,
        };
      });

      // Pack combos: delivery metadata stored at add-time; windows fetched separately by menu_pack_id
      packItems.forEach(({ product, quantity }) => {
        const possibleDates = computeDeliveryDates(product);
        const selectedDate = possibleDates[0];
        deliveryMap[product.id] = {
          product, quantity,
          possibleDates,
          selectedDate,
          deliveryDate: selectedDate,
          deliveryDateStr: toISODate(selectedDate),
          allWindows: [], windows: [], selectedWindow: null,
        };
      });

      const productIds   = nonPackItems.map(({ product }) => product.id).filter(Boolean);
      // Collect distinct menu_pack_ids from all pack combos in cart
      const menuPackIds  = [...new Set(packItems.map(({ product }) => product.menu_pack_id).filter(Boolean))];

      try {
        const [productWindowsRes, packWindowsRes] = await Promise.all([
          productIds.length > 0
            ? axios.get(`${API_URL}/menuRoutes/product-windows`, {
                params: { product_ids: productIds.join(',') },
                headers: authHeader,
              })
            : Promise.resolve({ data: [] }),
          menuPackIds.length > 0
            ? axios.get(`${API_URL}/menuRoutes/pack-windows`, {
                params: { menu_pack_ids: menuPackIds.join(',') },
                headers: authHeader,
              })
            : Promise.resolve({ data: [] }),
        ]);

        // Populate regular product windows
        const byProductId = {};
        productWindowsRes.data.forEach((tw) => {
          if (!byProductId[tw.product_id]) byProductId[tw.product_id] = [];
          byProductId[tw.product_id].push(tw);
        });

        // Populate pack windows (all combos of the same menu_pack_id share the same windows)
        const byMenuPackId = {};
        packWindowsRes.data.forEach((tw) => {
          if (!byMenuPackId[tw.menu_pack_id]) byMenuPackId[tw.menu_pack_id] = [];
          byMenuPackId[tw.menu_pack_id].push(tw);
        });

        Object.values(deliveryMap).forEach((entry) => {
          if (entry.product.isPack) {
            const all = byMenuPackId[entry.product.menu_pack_id] || [];
            entry.allWindows = all;
            entry.windows    = all.filter((tw) => windowMatchesDate(tw, entry.selectedDate));
          } else {
            const all = byProductId[entry.product.id] || [];
            entry.allWindows = all;
            entry.windows    = all.filter((tw) => windowMatchesDate(tw, entry.selectedDate));
          }
        });
      } catch (e) {
        console.error('Failed to load time windows', e);
      }

      const enrichedMap = assignWindowModes(deliveryMap);

      // Apply waiver filter inline — avoids a second setEnriched call that would flash the UI
      if (waiverActive) {
        Object.keys(enrichedMap).forEach(id => {
          const e = enrichedMap[id];
          const filtered = (e.socialWindows || []).filter(w => WAIVER_WINDOW_STARTS.some(s => w.start_time?.startsWith(s)));
          const sel = filtered.find(w => w.start_time === e.selectedWindow?.start_time) || filtered[0] || null;
          enrichedMap[id] = { ...e, socialWindows: filtered, selectedWindow: sel };
        });
      }

      setEnriched(enrichedMap);

      const needsSlotPick =
        Object.values(enrichedMap).some(({ product, possibleDates }) => product.is_kit || possibleDates.length > 1) ||
        Object.values(enrichedMap).some(({ socialWindows }) => socialWindows.length > 1);

      if (needsSlotPick) {
        setPhase('slot_select');
      } else {
        let groups = buildOrderGroups(enrichedMap);
        if (deliveryFeeItem && groups.length > 0) {
          groups[0] = {
            ...groups[0],
            items: [...groups[0].items, { product: deliveryFeeItem.product, quantity: deliveryFeeItem.quantity }],
            total: groups[0].total + parseFloat(deliveryFeeItem.product.price) * deliveryFeeItem.quantity,
          };
        }
        setOrderGroups(groups);
        setPhase('summary');
      }
    };

    load();
  }, []); // eslint-disable-line

  // Auto-evaluate non-promo offers (cumulative_spend, first_order, etc.) when summary phase begins
  useEffect(() => {
    if (phase !== 'summary' || !user?.customer_id || appliedOffer) return;
    const cartTotal = orderGroups.reduce((s, g) => s + g.total, 0);
    if (cartTotal <= 0) return;
    axios
      .post(`${API_URL}/offers/evaluate`, { cartTotal }, { headers: authHeader })
      .then(({ data }) => {
        const offer = data.offers?.[0];
        if (offer) setAppliedOffer(offer);
      })
      .catch(() => {});
  }, [phase]); // eslint-disable-line

  // Fetch wallet balance when summary phase begins; auto-fill if funds available
  useEffect(() => {
    if (phase !== 'summary' || !user?.customer_id) return;
    axios
      .get(`${API_URL}/wallet/balance`, { headers: authHeader })
      .then((res) => {
        setWalletBalance(res.data);
        const cash   = parseFloat(res.data.cash_balance)   || 0;
        const points = parseFloat(res.data.points_balance) || 0;
        if (cash > 0 || points > 0) {
          const total     = orderGroups.reduce((s, g) => s + g.total, 0);
          const autoCash   = r2(Math.min(cash, total));
          const autoPoints = r2(Math.min(points, Math.max(0, total - autoCash)));
          setWalletCashUsed(autoCash);
          setWalletPointsUsed(autoPoints);
        }
      })
      .catch(() => {});
  }, [phase]); // eslint-disable-line

  const handleSlotChange = (productId, idx) => {
    setEnriched((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], selectedWindow: prev[productId].socialWindows[parseInt(idx, 10)] },
    }));
  };

  // For common-mode dates: update selectedWindow for ALL items on that date
  const handleGroupWindowChange = (dateStr, idx) => {
    setEnriched((prev) => {
      const updated = { ...prev };
      Object.values(updated).forEach((entry) => {
        if (entry.deliveryDateStr === dateStr && entry.windowMode === 'common') {
          updated[entry.product.id] = {
            ...entry,
            selectedWindow: entry.socialWindows[parseInt(idx, 10)],
          };
        }
      });
      return updated;
    });
  };

  // For common-mode dates: move ALL items in the group to a new delivery date
  const handleGroupDateChange = (dateStr, dateIso) => {
    setEnriched((prev) => {
      const updated = { ...prev };
      Object.values(updated).forEach((entry) => {
        if (entry.deliveryDateStr !== dateStr || entry.windowMode !== 'common') return;
        let newDate = entry.possibleDates.find((d) => toISODate(d) === dateIso);
        if (!newDate) {
          const [y, m, d] = dateIso.split('-').map(Number);
          newDate = new Date(y, m - 1, d, 12, 0, 0, 0);
        }
        updated[entry.product.id] = {
          ...entry,
          selectedDate: newDate,
          deliveryDate: newDate,
          deliveryDateStr: dateIso,
          windows: entry.allWindows.filter((tw) => windowMatchesDate(tw, newDate)),
        };
      });
      return assignWindowModes(updated);
    });
  };

  const handleDateChange = (productId, dateIso) => {
    setEnriched((prev) => {
      const entry = prev[productId];
      if (!entry) return prev;
      let newDate = entry.possibleDates.find((d) => toISODate(d) === dateIso);
      if (!newDate) {
        const [y, m, d] = dateIso.split('-').map(Number);
        newDate = new Date(y, m - 1, d, 12, 0, 0, 0);
      }
      const filtered = entry.allWindows.filter((tw) => windowMatchesDate(tw, newDate));
      const updated = {
        ...prev,
        [productId]: { ...entry, selectedDate: newDate, deliveryDate: newDate, deliveryDateStr: dateIso, windows: filtered },
      };
      return assignWindowModes(updated);
    });
  };

  const handleScheduleChange = (productId, scheduleId) => {
    setEnriched((prev) => {
      const entry = prev[productId];
      if (!entry) return prev;
      const sched = (entry.schedules || []).find(s => s.id === scheduleId);
      const deliveryDays = sched?.delivery_days || null;
      const possibleDates = computeKitPossibleDates(entry.product, deliveryDays);
      const selectedDate = possibleDates[0] || addDays(new Date(), 1);
      const dateStr = toISODate(selectedDate);
      const filtered = entry.allWindows.filter(tw => windowMatchesDate(tw, selectedDate));
      const updated = {
        ...prev,
        [productId]: {
          ...entry,
          selectedScheduleId: scheduleId,
          possibleDates,
          selectedDate,
          deliveryDate: selectedDate,
          deliveryDateStr: dateStr,
          windows: filtered,
        },
      };
      return assignWindowModes(updated);
    });
  };

  const handleConfirmSlots = () => {
    const deliveryFeeItem = items.find(({ product }) => product.id === 1047) || null;
    const groups = buildOrderGroups(enriched);
    if (deliveryFeeItem && groups.length > 0) {
      groups[0] = {
        ...groups[0],
        items: [...groups[0].items, { product: deliveryFeeItem.product, quantity: deliveryFeeItem.quantity }],
        total: groups[0].total + parseFloat(deliveryFeeItem.product.price) * deliveryFeeItem.quantity,
      };
    }
    setOrderGroups(groups);
    setPhase('summary');
  };

  const cancelPending = (orderId) => {
    if (!orderId) return;
    axios.post(`${API_URL}/pay/cancel-pending`, { razorpay_order_id: orderId }, { headers: authHeader })
      .catch(() => {});
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const closeQrModal = () => {
    clearInterval(qrPollRef.current);
    const { qrId } = qrModal;
    setQrModal({ open: false, qrId: null, imageUrl: null, amount: 0 });
    if (qrId) {
      axios.post(`${API_URL}/pay/cancel-qr`, { qr_id: qrId }, { headers: authHeader }).catch(() => {});
    }
  };

  const handleQrCheckout = async () => {
    setOrderError(null);
    setPhase('placing');
    try {
      const totalAmount = orderGroups.reduce((s, g) => s + g.total, 0);
      const { data } = await axios.post(
        `${API_URL}/pay/create-qr`,
        {
          amount:         totalAmount,
          orderGroups:    orderGroups.map((g) => ({
            deliveryDateStr:   g.deliveryDateStr,
            deliveryDateLabel: g.deliveryDateLabel,
            slot:              g.slot,
            total:             g.total,
            items: g.items.map(buildItemPayload),
          })),
          customerId:     user?.customer_id || null,
          customerName:   customerInfo?.customer_name || null,
          deliveryCustomerAddressId: selectedAddressId || null,
          salesChannelId: isLive ? liveSalesChannelId : null,
          mode:           isLive ? 'live' : 'prebook',
        },
        { headers: authHeader }
      );

      setPhase('summary');
      setQrModal({ open: true, qrId: data.qr_id, imageUrl: data.image_url, amount: data.amount });

      qrPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${API_URL}/pay/qr-status/${data.qr_id}`,
            { headers: authHeader }
          );
          if (statusRes.data.paid) {
            clearInterval(qrPollRef.current);
            clearCart();
            setConfirmations(statusRes.data.confirmations || []);
            setQrModal({ open: false, qrId: null, imageUrl: null, amount: 0 });
            setPhase('confirmed');
          }
        } catch (_) {}
      }, 3000);
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Failed to generate QR code. Please try again.');
      setPhase('summary');
    }
  };

  const offerEffectiveValue = (offer) => {
    const cartTotal = orderGroups.reduce((s, g) => s + g.total, 0);
    if (offer.reward_type === 'wallet_credit_pct')
      return Math.round(cartTotal * parseFloat(offer.reward_value) / 100 * 100) / 100;
    if (offer.reward_type === 'wallet_credit')
      return parseFloat(offer.reward_value || 0);
    return offer.discount || 0;
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoError('');
    setPromoLoading(true);
    try {
      const cartTotal = orderGroups.reduce((s, g) => s + g.total, 0);
      const { data } = await axios.post(
        `${API_URL}/offers/evaluate`,
        { cartTotal, promoCode: promoCode.trim() },
        { headers: authHeader }
      );
      const offer = data.offers?.[0];
      if (!offer) {
        setPromoError('Invalid or expired promo code.');
      } else if (appliedOffer && appliedOffer.rule_type !== 'promo_code') {
        // Conflict: auto-applied offer already exists — show both, default to better value
        const autoVal  = offerEffectiveValue(appliedOffer);
        const promoVal = offerEffectiveValue(offer);
        if (promoVal >= autoVal) {
          setAlternateOffer(appliedOffer);
          setAppliedOffer(offer);
        } else {
          setAlternateOffer(offer);
        }
        setPromoCode('');
        setPromoError('');
      } else {
        setAppliedOffer(offer);
        setPromoCode('');
        setPromoError('');
      }
    } catch (err) {
      setPromoError(err.response?.data?.error || 'Failed to apply promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSwitchOffer = () => {
    setAppliedOffer(alternateOffer);
    setAlternateOffer(appliedOffer);
  };

  const handleRemoveOffer = () => {
    setAppliedOffer(null);
    setAlternateOffer(null);
    setPromoCode('');
    setPromoError('');
  };

  const handlePlaceOrders = async () => {
    setPhase('placing');
    setOrderError(null);

    const totalAmount  = orderGroups.reduce((s, g) => s + g.total, 0);
    const discountAmt  = appliedOffer?.discount || 0;
    const payable      = r2(Math.max(0, totalAmount - discountAmt));
    const usedCash     = r2(Math.min(walletCashUsed   || 0, payable));
    const usedPoints   = r2(Math.min(walletPointsUsed || 0, Math.max(0, payable - usedCash)));
    const razorpayDue  = r2(Math.max(0, payable - usedCash - usedPoints));

    const orderPayload = {
      amount:           totalAmount,
      offerId:          appliedOffer?.id   || null,
      offerDiscount:    discountAmt,
      walletCashAmount:   usedCash,
      walletPointsAmount: usedPoints,
      orderGroups: orderGroups.map((g) => ({
        deliveryDateStr:   g.deliveryDateStr,
        deliveryDateLabel: g.deliveryDateLabel,
        slot:              g.slot,
        total:             g.total,
        items: g.items.map(buildItemPayload),
      })),
      customerId:     user?.customer_id || null,
      customerName:   customerInfo?.customer_name || null,
      deliveryCustomerAddressId: selectedAddressId || null,
      salesChannelId: isLive ? liveSalesChannelId : null,
      mode:           isLive ? 'live' : 'prebook',
    };

    // ── Pure wallet / offer-covered: no Razorpay modal needed ─────
    if (razorpayDue < 0.01 && (usedCash > 0 || usedPoints > 0 || discountAmt > 0)) {
      try {
        const { data } = await axios.post(`${API_URL}/pay/create-order`, orderPayload, { headers: authHeader });
        if (data.wallet_only) {
          clearCart();
          setConfirmations(data.confirmations);
          setPhase('confirmed');
          return;
        }
      } catch (err) {
        setOrderError(err.response?.data?.error || 'Failed to place order. Please try again.');
        setPhase('summary');
        return;
      }
    }

    // ── Razorpay (full or split) ───────────────────────────────
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setOrderError('Failed to load payment SDK. Please check your connection and try again.');
      setPhase('summary');
      return;
    }

    try {
      setPendingOrderId(null);
      const { data } = await axios.post(`${API_URL}/pay/create-order`, orderPayload, { headers: authHeader });

      setPendingOrderId(data.razorpay_order_id);

      const options = {
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:        'Local Heros Foods Pvt Ltd',
        description: `${orderGroups.length} order${orderGroups.length > 1 ? 's' : ''} · ${orderGroups.reduce((s, g) => s + g.items.reduce((a, i) => a + i.quantity, 0), 0)} items`,
        prefill: {
          name:    customerInfo?.customer_name || '',
          contact: user?.phone || '',
        },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            const res = await axios.post(
              `${API_URL}/pay/verify-and-place`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              },
              { headers: authHeader }
            );
            clearCart();
            setConfirmations(res.data.confirmations);
            setPhase('confirmed');
          } catch (err) {
            setOrderError(err.response?.data?.error || 'Payment received but order placement failed. Please contact support.');
            setPhase('summary');
          }
        },
        modal: {
          ondismiss: () => {
            cancelPending(data.razorpay_order_id);
            setPhase('summary');
          },
        },
        config: {
          display: {
            blocks: {
              upiQr: {
                name: 'Pay via UPI QR Code',
                instruments: [{ method: 'upi', flows: ['qr'] }],
              },
            },
            sequence: ['block.upiQr'],
            preferences: { show_default_blocks: true },
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setOrderError(`Payment failed: ${response.error.description}`);
        setPhase('summary');
      });
      rzp.open();
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPhase('summary');
    }
  };

  const handleBypassSubmit = async () => {
    setPhase('placing');
    setOrderError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/pay/bypass-place-order`,
        {
          orderGroups: orderGroups.map((g) => ({
            deliveryDateStr:   g.deliveryDateStr,
            deliveryDateLabel: g.deliveryDateLabel,
            slot:              g.slot,
            total:             g.total,
            items: g.items.map(buildItemPayload),
          })),
          customerId:     user?.customer_id || null,
          customerName:   customerInfo?.customer_name || null,
          deliveryCustomerAddressId: selectedAddressId || null,
          salesChannelId: isLive ? liveSalesChannelId : null,
          mode:           isLive ? 'live' : 'prebook',
        },
        { headers: authHeader }
      );
      clearCart();
      setConfirmations(data.confirmations);
      setPhase('confirmed');
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Failed to place order. Please try again.');
      setPhase('summary');
    }
  };

  // Clamp wallet inputs to valid range whenever they change
  const handleCashInput = (val) => {
    const totalAmount = orderGroups.reduce((s, g) => s + g.total, 0);
    const maxCash = parseFloat(walletBalance?.cash_balance || 0);
    const parsed = parseFloat(val) || 0;
    setWalletCashUsed(r2(Math.max(0, Math.min(parsed, maxCash, totalAmount))));
  };

  const handlePointsInput = (val) => {
    const totalAmount = orderGroups.reduce((s, g) => s + g.total, 0);
    const maxPoints = parseFloat(walletBalance?.points_balance || 0);
    const parsed = parseFloat(val) || 0;
    const remaining = Math.max(0, totalAmount - (walletCashUsed || 0));
    setWalletPointsUsed(r2(Math.max(0, Math.min(parsed, maxPoints, remaining))));
  };

  /* ── Empty cart ── */
  if (items.length === 0 && phase !== 'confirmed') {
    return (
      <div className="checkout-empty">
        <p>Your cart is empty.</p>
        <button className="checkout-back-btn" onClick={() => navigate('/home')}>Browse Products</button>
      </div>
    );
  }

  /* ── Loading ── */
  if (phase === 'loading') {
    return (
      <div className="checkout-empty">
        <p style={{ color: '#999' }}>Calculating delivery dates…</p>
      </div>
    );
  }

  /* ── Confirmed ── */
  if (phase === 'confirmed') {
    return (
      <div className="checkout-confirmation">
        <div className="checkout-confirmation-icon">&#10003;</div>
        <h2 className="checkout-confirmation-title">
          {confirmations.length === 1 ? 'Order Placed!' : `${confirmations.length} Orders Placed!`}
        </h2>
        {confirmations.map((c, i) => (
          <p key={i} className="checkout-confirmation-msg">
            {isLive
              ? <>Order <strong>{c.order_no}</strong> has been confirmed.</>
              : <>Order <strong>{c.order_no}</strong> — delivery on <strong>{c.delivery_date}</strong>
                 {c.slot !== '—' && <>, slot <strong>{c.slot}</strong></>}.</>
            }
          </p>
        ))}
        <p className="checkout-confirmation-cod">{bypassPayment ? 'Your order has been placed.' : 'Payment successful! Your order is confirmed.'}</p>
        <button className="checkout-back-btn" onClick={() => navigate('/categories')}>Continue Shopping</button>
      </div>
    );
  }

  /* ── Slot selection ── */
  if (phase === 'slot_select' && enriched) {
    return (
      <div className="checkout-container">
        <div className="checkout-header">
          <button className="checkout-back-link" onClick={() => navigate('/cart')}>&#8592; Back to Cart</button>
          <h2 className="checkout-title">Select Delivery Date &amp; Slot</h2>
        </div>
        <div className="slot-select-body">
          {(() => {
            const regularEntries  = Object.values(enriched).filter((e) => e.product.id !== 1047);
            const kitEntriesUI    = regularEntries.filter(e => e.product.is_kit);
            const nonKitEntriesUI = regularEntries.filter(e => !e.product.is_kit && !e.product.isPack);
            const hasMixedCartUI  = kitEntriesUI.length > 0 && nonKitEntriesUI.length > 0;
            const minKitEntryUI   = hasMixedCartUI ? getMinKitEntry(enriched) : null;

            // In a mixed cart, exclude non-kit items from the interactive cards
            const displayEntries = hasMixedCartUI
              ? regularEntries.filter(e => e.product.is_kit || e.product.isPack)
              : regularEntries;

            // Derive one card per date for common-mode groups
            const commonGroups = {};
            displayEntries.filter((e) => e.windowMode === 'common').forEach((e) => {
              if (!commonGroups[e.deliveryDateStr]) {
                commonGroups[e.deliveryDateStr] = {
                  dateStr: e.deliveryDateStr, date: e.deliveryDate,
                  socialWindows: e.socialWindows, selectedWindow: e.selectedWindow,
                  entries: [],
                };
              }
              commonGroups[e.deliveryDateStr].entries.push(e);
            });

            // Social-mode items that still need user input
            const socialItems = displayEntries.filter(
              (e) => e.windowMode === 'social' && (e.product.is_kit || e.possibleDates.length > 1 || e.socialWindows.length > 1)
            );

            return (
              <>
                {/* Non-kit read-only card: shown in mixed carts, date/window auto-set from earliest kit */}
                {hasMixedCartUI && minKitEntryUI && nonKitEntriesUI.length > 0 && (
                  <div className="slot-item-card slot-item-card--auto">
                    <div className="slot-item-info">
                      <span className="slot-item-placeholder">&#128230;</span>
                      <span className="slot-item-name">Other Items</span>
                    </div>
                    <p className="slot-group-items">
                      {nonKitEntriesUI.map(e => `${e.product.name} \xd7${e.quantity}`).join(' \xb7 ')}
                    </p>
                    <div className="slot-auto-info">
                      <span className="slot-auto-label">Delivered with your earliest kit on:</span>
                      <span className="slot-window-pill">&#128337; {formatDate(minKitEntryUI.deliveryDate)}</span>
                      {minKitEntryUI.selectedWindow && (
                        <span className="slot-window-pill">&#128336; {formatSlot(minKitEntryUI.selectedWindow)}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Common-mode: one card per date showing all items, group-level date + window selector */}
                {Object.values(commonGroups).map((grp) => {
                  const hasDateChoice = grp.entries.some((e) => e.possibleDates.length > 1);
                  return (
                    <div key={grp.dateStr} className="slot-item-card">
                      <div className="slot-item-info">
                        <span className="slot-item-name">&#128337; {formatDate(grp.date)}</span>
                        {grp.socialWindows.length === 1 && (
                          <span className="slot-window-pill">&#128336; {formatSlot(grp.selectedWindow)}</span>
                        )}
                      </div>
                      <p className="slot-group-items">
                        {grp.entries.map((e) => `${e.product.name} ×${e.quantity}`).join(' · ')}
                      </p>
                      <div className="slot-sections">
                        {hasDateChoice && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Date</span>
                            <div className="slot-date-btns">
                              {grp.entries[0].possibleDates.map((d) => {
                                const iso = toISODate(d);
                                return (
                                  <button
                                    key={iso}
                                    className={`slot-date-btn${grp.dateStr === iso ? ' slot-date-btn--active' : ''}`}
                                    onClick={() => handleGroupDateChange(grp.dateStr, iso)}
                                  >
                                    {formatDate(d)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {grp.socialWindows.length > 1 && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Window</span>
                            <div className="slot-date-btns">
                              {grp.socialWindows.map((w, i) => (
                                <button
                                  key={i}
                                  className={`slot-date-btn${
                                    grp.selectedWindow?.start_time === w.start_time &&
                                    grp.selectedWindow?.end_time   === w.end_time
                                      ? ' slot-date-btn--active' : ''
                                  }`}
                                  onClick={() => handleGroupWindowChange(grp.dateStr, i)}
                                >
                                  {formatSlot(w)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Social-mode: per-item cards for date choice and/or individual window choice */}
                {socialItems.map((entry) => {
                  const needsDateChoice   = entry.possibleDates.length > 1;
                  const needsWindowChoice = entry.socialWindows.length > 1;
                  return (
                    <div key={entry.product.id} className="slot-item-card">
                      <div className="slot-item-info">
                        {entry.product.image_url
                          ? <img className="slot-item-img" src={entry.product.image_url} alt={entry.product.name} />
                          : <span className="slot-item-placeholder">&#128230;</span>}
                        <span className="slot-item-name">{entry.product.name}</span>
                        <span className="slot-item-qty">&#215; {entry.quantity}</span>
                        {!needsWindowChoice && entry.selectedWindow && (
                          <span className="slot-window-pill">&#128336; {formatSlot(entry.selectedWindow)}</span>
                        )}
                      </div>
                      {entry.product.is_kit && (
                        <p className="slot-kit-schedule">
                          Delivered {((entry.schedules || []).find(s => s.id === entry.selectedScheduleId)?.delivery_days || entry.product.kit_delivery_days || []).map(d => DAY_ABBR[d]).join(' & ')} each week · first delivery sets the schedule
                        </p>
                      )}
                      <div className="slot-sections">
                        {entry.product.is_kit ? (
                          <>
                            {(entry.schedules || []).length > 1 && (
                              <div className="slot-section slot-section--wide">
                                <span className="slot-section-label">Delivery Schedule</span>
                                <div className="slot-kit-date-grid">
                                  {entry.schedules.map((sched) => (
                                    <button
                                      key={sched.id}
                                      className={`slot-kit-date-pill${entry.selectedScheduleId === sched.id ? ' slot-kit-date-pill--active' : ''}`}
                                      onClick={() => handleScheduleChange(entry.product.id, sched.id)}
                                    >
                                      {sched.schedule_name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="slot-section slot-section--wide">
                              <span className="slot-section-label">Choose First Delivery Date</span>
                              <div className="slot-kit-date-grid">
                                {entry.possibleDates.map((d) => {
                                  const iso = toISODate(d);
                                  return (
                                    <button
                                      key={iso}
                                      className={`slot-kit-date-pill${entry.deliveryDateStr === iso ? ' slot-kit-date-pill--active' : ''}`}
                                      onClick={() => handleDateChange(entry.product.id, iso)}
                                    >
                                      {formatKitDate(d)}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        ) : needsDateChoice ? (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Date</span>
                            <div className="slot-date-btns">
                              {entry.possibleDates.map((d) => {
                                const iso = toISODate(d);
                                return (
                                  <button
                                    key={iso}
                                    className={`slot-date-btn${entry.deliveryDateStr === iso ? ' slot-date-btn--active' : ''}`}
                                    onClick={() => handleDateChange(entry.product.id, iso)}
                                  >
                                    {formatDate(d)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                        {needsWindowChoice && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Window</span>
                            <div className="slot-date-btns">
                              {entry.socialWindows.map((w, i) => (
                                <button
                                  key={i}
                                  className={`slot-date-btn${
                                    entry.selectedWindow?.start_time === w.start_time &&
                                    entry.selectedWindow?.end_time   === w.end_time
                                      ? ' slot-date-btn--active' : ''
                                  }`}
                                  onClick={() => handleSlotChange(entry.product.id, i)}
                                >
                                  {formatSlot(w)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          <div className="slot-actions">
            <button className="checkout-place-btn" style={{ maxWidth: 320 }} onClick={handleConfirmSlots}>
              Continue to Summary &#8594;
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Summary / Placing ── */
  const totalAll    = orderGroups.reduce((s, g) => s + g.total, 0);
  const totalUnits  = orderGroups.reduce((s, g) => s + g.items.reduce((a, i) => a + i.quantity, 0), 0);
  const discountAmt = appliedOffer?.discount || 0;
  const payable     = r2(Math.max(0, totalAll - discountAmt));

  const usedCash    = r2(Math.min(walletCashUsed   || 0, payable));
  const usedPoints  = r2(Math.min(walletPointsUsed || 0, Math.max(0, payable - usedCash)));
  const razorpayDue = r2(Math.max(0, payable - usedCash - usedPoints));

  const hasWallet  = walletBalance && (
    parseFloat(walletBalance.cash_balance) > 0 || parseFloat(walletBalance.points_balance) > 0
  );

  const btnLabel = phase === 'placing'
    ? 'Processing…'
    : razorpayDue < 0.01 && (usedCash > 0 || usedPoints > 0 || discountAmt > 0)
      ? 'Place Order'
      : (usedCash > 0 || usedPoints > 0)
        ? `Pay ₹${razorpayDue.toFixed(2)} via Razorpay`
        : orderGroups.length > 1
          ? `Pay for ${orderGroups.length} Orders`
          : 'Pay Now';

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <button className="checkout-back-link" onClick={() => navigate('/cart')}>&#8592; Back to Cart</button>
        <h2 className="checkout-title">Order Summary</h2>
        {orderGroups.length > 1 && (
          <p className="checkout-multi-note">
            Your cart will be split into {orderGroups.length} orders by delivery date / slot.
          </p>
        )}
      </div>

      <div className="checkout-summary-layout">
        {/* Left: order group cards */}
        <div className="checkout-groups">
          {orderGroups.map((group, gi) => (
            <div key={gi} className="checkout-group-card">
              <div className="checkout-group-header">
                {orderGroups.length > 1 && (
                  <span className="checkout-group-badge">Order {gi + 1}</span>
                )}
                {!isLive && <span className="checkout-group-date">&#128337; {group.deliveryDateLabel}</span>}
                {!isLive && group.slot !== '—' && (
                  <span className="checkout-group-slot">&#128336; {group.slot}</span>
                )}
                {!isLive && group.items.some(({ product }) => product.is_kit) && (
                  <span className="checkout-group-kit-note">&#128230; Kit — child deliveries scheduled at checkout</span>
                )}
              </div>
              <div className="checkout-items">
                {group.items.map(({ product, quantity }) => {
                  const lineTotal = product.price != null ? parseFloat(product.price) * quantity : null;
                  return (
                    <div key={product.id} className="checkout-item-row">
                      <div className="checkout-item-image">
                        {product.image_url
                          ? <img src={product.image_url} alt={product.name} />
                          : <span className="checkout-item-placeholder">&#128230;</span>}
                      </div>
                      <div className="checkout-item-info">
                        <p className="checkout-item-name">{product.name}</p>
                        {product.description && <DescriptionCell text={product.description} className="checkout-item-desc" />}
                        <p className="checkout-item-unit">{formatPrice(product.price)} each</p>
                      </div>
                      <div className="checkout-item-qty">
                        <span className="checkout-qty-label">Qty</span>
                        <span className="checkout-qty-value">{quantity}</span>
                      </div>
                      <div className="checkout-item-total">
                        {lineTotal != null ? formatPrice(lineTotal) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
              {orderGroups.length > 1 && (
                <div className="checkout-group-subtotal">
                  <span>Subtotal</span>
                  <span>{formatPrice(group.total)}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right: address + wallet + total */}
        <div className="checkout-summary-panel">
          <div className="checkout-card">
            <h3 className="checkout-card-title">&#128205; Delivery Address</h3>
            {!user?.customer_id ? (
              <p className="checkout-address-missing">No customer account linked.</p>
            ) : !selectedAddress ? (
              <p className="checkout-address-loading">Loading address…</p>
            ) : (
              <div className="checkout-addr-detail">
                <div className="checkout-addr-label-row">
                  <span className="checkout-addr-label">{selectedAddress.label}</span>
                  {selectedAddress.is_shared && <span className="checkout-addr-badge checkout-addr-badge--shared">Shared</span>}
                  {selectedAddress.waive_delivery_fee && <span className="checkout-addr-badge checkout-addr-badge--free">Free Delivery</span>}
                </div>
                {selectedAddress.location_name && <div className="checkout-addr-location">{selectedAddress.location_name}</div>}
                <div className="checkout-addr-text">
                  {selectedAddress.address}
                  {selectedAddress.pincode && !/\d{6}/.test(selectedAddress.address)
                    ? ` - ${selectedAddress.pincode}` : ''}
                </div>
                {selectedAddress.notes && <div className="checkout-addr-notes">{selectedAddress.notes}</div>}
              </div>
            )}
          </div>

          {/* Wallet card — hidden for bypass-payment users */}
          {hasWallet && !bypassPayment && (
            <div className="checkout-card checkout-wallet-card">
              <h3 className="checkout-card-title">&#128179; Pay from Wallet</h3>

              {parseFloat(walletBalance.cash_balance) > 0 && (
                <div className="checkout-wallet-row">
                  <div className="checkout-wallet-avail">
                    <span className="checkout-wallet-label">Cash</span>
                    <span className="checkout-wallet-bal">
                      Available: {formatPrice(walletBalance.cash_balance)}
                    </span>
                  </div>
                  <input
                    type="number"
                    className="checkout-wallet-input"
                    min="0"
                    max={parseFloat(walletBalance.cash_balance)}
                    step="0.01"
                    value={walletCashUsed}
                    onChange={(e) => handleCashInput(e.target.value)}
                  />
                </div>
              )}

              {parseFloat(walletBalance.points_balance) > 0 && (
                <div className="checkout-wallet-row">
                  <div className="checkout-wallet-avail">
                    <span className="checkout-wallet-label">Reward Points</span>
                    <span className="checkout-wallet-bal">
                      Available: {parseFloat(walletBalance.points_balance).toFixed(0)} pts
                    </span>
                  </div>
                  <input
                    type="number"
                    className="checkout-wallet-input"
                    min="0"
                    max={parseFloat(walletBalance.points_balance)}
                    step="1"
                    value={walletPointsUsed}
                    onChange={(e) => handlePointsInput(e.target.value)}
                  />
                </div>
              )}

              {(usedCash > 0 || usedPoints > 0) && (
                <div className="checkout-wallet-deduct-row">
                  <span>Wallet deduction</span>
                  <span>- {formatPrice(usedCash + usedPoints)}</span>
                </div>
              )}

              {razorpayDue > 0.01 && (usedCash > 0 || usedPoints > 0) && (
                <div className="checkout-razorpay-due-row">
                  <span>Pay via Razorpay</span>
                  <span className="checkout-razorpay-due-amt">{formatPrice(razorpayDue)}</span>
                </div>
              )}
            </div>
          )}

          {/* Promo code card — hidden for bypass-payment users */}
          {!bypassPayment && (
            <div className="checkout-card checkout-promo-card">
              <h3 className="checkout-card-title">Promo Code</h3>
              {appliedOffer && alternateOffer ? (
                <div className="checkout-offer-conflict">
                  <p className="checkout-offer-conflict-hint">Best offer selected — tap to switch:</p>
                  {[appliedOffer, alternateOffer].map((offer, idx) => {
                    const isActive = idx === 0;
                    const cartTotal = orderGroups.reduce((s, g) => s + g.total, 0);
                    let valueLabel = '';
                    if (offer.reward_type === 'wallet_credit_pct') {
                      const amt = Math.round(cartTotal * parseFloat(offer.reward_value) / 100 * 100) / 100;
                      valueLabel = `+${formatPrice(amt)} reward points (${offer.reward_value}% of order)`;
                    } else if (offer.reward_type === 'wallet_credit') {
                      valueLabel = `+${formatPrice(parseFloat(offer.reward_value))} reward points`;
                    } else if (offer.discount > 0) {
                      valueLabel = `- ${formatPrice(offer.discount)} discount`;
                    }
                    return (
                      <div
                        key={offer.id}
                        className={`checkout-offer-option${isActive ? ' checkout-offer-option--active' : ''}`}
                        onClick={isActive ? undefined : handleSwitchOffer}
                      >
                        <div className="checkout-offer-option-info">
                          <span className="checkout-offer-option-title">
                            {isActive && <span className="checkout-offer-option-check">✓ </span>}
                            {offer.title}
                          </span>
                          {valueLabel && <span className="checkout-offer-option-value">{valueLabel}</span>}
                        </div>
                        {!isActive && <span className="checkout-offer-option-switch">Use this</span>}
                      </div>
                    );
                  })}
                  <button className="checkout-promo-remove checkout-offer-remove-all" onClick={handleRemoveOffer}>Remove all offers</button>
                </div>
              ) : appliedOffer ? (
                <div className="checkout-promo-applied">
                  <div className="checkout-promo-applied-info">
                    <span className="checkout-promo-applied-title">{appliedOffer.title}</span>
                    {appliedOffer.discount > 0 && (
                      <span className="checkout-promo-applied-disc">- {formatPrice(appliedOffer.discount)}</span>
                    )}
                    {appliedOffer.reward_type === 'wallet_credit' && (
                      <span className="checkout-promo-wallet-credit">
                        +{formatPrice(parseFloat(appliedOffer.reward_value))} reward points will be credited after order
                      </span>
                    )}
                    {appliedOffer.reward_type === 'wallet_credit_pct' && (() => {
                      const creditAmt = Math.round(totalAll * parseFloat(appliedOffer.reward_value) / 100 * 100) / 100;
                      return (
                        <span className="checkout-promo-wallet-credit">
                          +{formatPrice(creditAmt)} reward points will be credited ({appliedOffer.reward_value}% of order)
                        </span>
                      );
                    })()}
                  </div>
                  <button className="checkout-promo-remove" onClick={handleRemoveOffer}>Remove</button>
                </div>
              ) : null}
              {!alternateOffer && (!appliedOffer || appliedOffer.rule_type !== 'promo_code') && (
                <div className="checkout-promo-row">
                  <input
                    type="text"
                    className="checkout-promo-input"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    disabled={promoLoading}
                  />
                  <button
                    className="checkout-promo-btn"
                    onClick={handleApplyPromo}
                    disabled={!promoCode.trim() || promoLoading}
                  >
                    {promoLoading ? '…' : 'Apply'}
                  </button>
                </div>
              )}
              {promoError && <p className="checkout-promo-error">{promoError}</p>}
            </div>
          )}

          <div className="checkout-card checkout-total-card">
            <div className="checkout-total-row">
              <span>
                Total ({totalUnits} unit{totalUnits !== 1 ? 's' : ''}
                {orderGroups.length > 1 ? `, ${orderGroups.length} orders` : ''})
              </span>
              <span className="checkout-total-amount">{formatPrice(totalAll)}</span>
            </div>
            {discountAmt > 0 && (
              <div className="checkout-total-row checkout-discount-row">
                <span>Discount ({appliedOffer?.title})</span>
                <span className="checkout-discount-amount">- {formatPrice(discountAmt)}</span>
              </div>
            )}
            {discountAmt > 0 && (
              <div className="checkout-total-row checkout-payable-row">
                <span>You Pay</span>
                <span className="checkout-total-amount">{formatPrice(payable)}</span>
              </div>
            )}
            {orderError && <p className="checkout-order-error">{orderError}</p>}
            {bypassPayment ? (
              <button
                className="checkout-place-btn"
                onClick={handleBypassSubmit}
                disabled={phase === 'placing'}
              >
                {phase === 'placing' ? 'Placing Order…' : 'Submit Order'}
              </button>
            ) : (
              <>
                <button
                  className="checkout-place-btn"
                  onClick={handlePlaceOrders}
                  disabled={phase === 'placing'}
                >
                  {btnLabel}
                </button>
                {isStorePartner && isMobile && (
                  <button
                    className="checkout-place-btn checkout-qr-btn"
                    onClick={handleQrCheckout}
                    disabled={phase === 'placing'}
                  >
                    Pay via UPI QR — {formatPrice(totalAll)}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {qrModal.open && (
        <div className="checkout-qr-overlay">
          <div className="checkout-qr-modal">
            <h3 className="checkout-qr-title">Scan to Pay {formatPrice(qrModal.amount)}</h3>
            <img src={qrModal.imageUrl} alt="UPI QR Code" className="checkout-qr-img" />
            <p className="checkout-qr-waiting">Waiting for payment…</p>
            <button className="checkout-qr-cancel" onClick={closeQrModal}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
